import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import tributeImage from '../assets/images/grandpa.jpeg';
import '../styles/Tribute.css';

gsap.registerPlugin(ScrollTrigger);

const Tribute = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);
  const quoteRef = useRef(null);
  const dividerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const section = sectionRef.current;
      
      // Image fade and scale animation
      gsap.from(imageRef.current, {
        scrollTrigger: {
          trigger: section,
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        },
        scale: 0.8,
        opacity: 0,
        duration: 1.2,
        ease: 'power3.out'
      });

      // Divider line animation
      gsap.from(dividerRef.current, {
        scrollTrigger: {
          trigger: section,
          start: 'top 65%',
          toggleActions: 'play none none reverse'
        },
        scaleX: 0,
        duration: 1,
        ease: 'power2.out',
        delay: 0.3
      });

      // Text content stagger animation
      gsap.from(textRef.current.children, {
        scrollTrigger: {
          trigger: textRef.current,
          start: 'top 90%',
          toggleActions: 'play none none reverse'
        },
        y: 60,
        opacity: 0,
        stagger: 0.2,
        duration: 0.9,
        ease: 'power3.out',
        delay: 0.5
      });

      // Quote animation
      gsap.from(quoteRef.current, {
        scrollTrigger: {
          trigger: quoteRef.current,
          start: 'top 95%',
          toggleActions: 'play none none reverse'
        },
        y: 40,
        duration: 1,
        ease: 'power3.out',
        delay: 0.8
      });

      // Floating animation for the image
      gsap.to(imageRef.current, {
        y: -15,
        duration: 3,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="tribute" id="tribute">
      <div className="tribute-container">
        <div className="tribute-content">
          <div ref={imageRef} className="tribute-image-wrapper">
            <div className="image-frame">
              <img src={tributeImage} alt="In loving memory" className="tribute-image" />
              <div className="image-glow"></div>
            </div>
          </div>

          <div ref={dividerRef} className="tribute-divider"></div>

          <div ref={textRef} className="tribute-text">
            <h2 className="tribute-title">In Loving Memory</h2>
            <h3 className="tribute-subtitle">A Tribute to My Grandfather</h3>
            
            <div className="tribute-description">
              <p>
                To the man who shaped my world with wisdom, kindness, and unconditional love.
                Your guidance illuminated my path, and your strength became my inspiration.
              </p>
              <p>
                Every achievement I reach carries your spirit, every challenge I overcome 
                reflects the resilience you taught me. Though you're no longer here, your 
                legacy lives on in everything I do.
              </p>
              <p>
                Thank you for the laughter, the lessons, and the countless memories that 
                continue to warm my heart. You will forever be my guiding star.
              </p>
            </div>

            <div ref={quoteRef} className="tribute-quote">
              <div className="quote-mark">"</div>
              <p>Forever in my heart, always my inspiration</p>
              <div className="quote-mark closing">"</div>
            </div>

            <div className="tribute-dedication">
              <div className="heart-icon">♥</div>
              <p>Rest in peace, Grandpa</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tribute;
